import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Selectors } from "../_reducers/app.reducer";
import { HorizontalSingleSelectFilter } from "../_components/HorizontalFilterDropdown";
import { deviceLogsActions } from "../_actions/deviceLogs.actions";
import { LogsHelper } from "../_helpers";
import {
    ADVANCED_UI_LOG_TYPES,
    filterDisabled,
    nullValues,
    UI_LOG_TYPES,
} from "../_constants";
import { appPermissions } from "../_constants/permissions.constants";

const buildOption = (value, label) => ({
    value,
    label: label === undefined ? value : label,
});

const allOption = () => buildOption(filterDisabled.number, filterDisabled.all);

export function DeviceLogsTypeStatusUserFilters() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const users        = useSelector(Selectors.getUsers);
    const devices      = useSelector(Selectors.getSelectedLocationDevices);
    const filterType   = useSelector(Selectors.getDeviceLogsFilterType);
    const filterStatus = useSelector(Selectors.getDeviceLogsFilterTypeStatus);
    const filterUser   = useSelector(Selectors.getDeviceLogsFilterUser);
    const permission   = useSelector(Selectors.getPermissionSelectedLocation);
    const everouUser   = useSelector(Selectors.getEverouUser);
    const locationInfo = useSelector(Selectors.getLocationInfo);

    const canSeeAdvancedActivity = appPermissions.canUserSeeAdvancedActivity(
        permission,
        everouUser?.isSat,
    );
    const isBarrierEnabled = !!locationInfo?.isBarrierEnabled;

    const handleChangeFilterUser       = v => dispatch(deviceLogsActions.setFilterUser(v));
    const handleChangeFilterTypeStatus = v => dispatch(deviceLogsActions.setFilterTypeStatus(v));
    const handleChangeFilterType       = v => dispatch(deviceLogsActions.setFilterType(v));

    const typeOptions = buildUiTypeFilterOptions(devices);
    const userOptions = buildUiUserFilterOptions(users);

    const visibleTypeValues = useMemo(
        () => typeOptions.map(o => o.value),
        [typeOptions],
    );

    useEffect(() => {
        if (
            filterType !== filterDisabled.number &&
            !visibleTypeValues.includes(filterType)
        ) {
            dispatch(deviceLogsActions.setFilterType(filterDisabled.number));
        }
    }, [filterType, visibleTypeValues, dispatch]);

    const isStatusFilterAvailable = filterType !== filterDisabled.number;
    const statusOptions = isStatusFilterAvailable
        ? buildUiLogStatusFilterOptions()
        : [];
    const showStatus = isStatusFilterAvailable && statusOptions.length > 1;
    const showType = typeOptions.length > 1;

    return (
        <>
            <HorizontalSingleSelectFilter
                label={t("common.user")}
                options={userOptions}
                value={filterUser}
                onChange={handleChangeFilterUser}
                isActive={filterUser !== filterDisabled.number}
                isSearchable
            />
            {showType && (
                <HorizontalSingleSelectFilter
                    label={t("common.type")}
                    options={typeOptions}
                    value={filterType}
                    onChange={handleChangeFilterType}
                    isActive={filterType !== filterDisabled.number}
                />
            )}
            {showStatus && (
                <HorizontalSingleSelectFilter
                    label={t("common.status")}
                    options={statusOptions}
                    value={filterStatus}
                    onChange={handleChangeFilterTypeStatus}
                    isActive={filterStatus !== filterDisabled.number}
                />
            )}
        </>
    );

    function buildUiLogStatusFilterOptions() {
        const statusesObjectForType = LogsHelper.getStatusesForUiLogType(filterType);

        if (typeof statusesObjectForType === "string") {
            return [
                buildOption(
                    LogsHelper.getStatusLabelFromLog(
                        filterType,
                        statusesObjectForType,
                    ),
                ),
            ];
        }

        const uiStatuses = [];
        for (let statusId in statusesObjectForType) {
            uiStatuses.push(
                buildOption(
                    statusId,
                    LogsHelper.getStatusLabelFromLog(filterType, statusId),
                ),
            );
        }

        if (uiStatuses.length !== 1) uiStatuses.unshift(allOption());

        return uiStatuses;
    }

    function buildUiTypeFilterOptions(devices) {
        const logTypes = LogsHelper.getUiLogTypesFromDeviceTypes(devices)
            .filter(logType =>
                canSeeAdvancedActivity ||
                !ADVANCED_UI_LOG_TYPES.includes(logType),
            )
            .filter(logType =>
                isBarrierEnabled || logType !== UI_LOG_TYPES.ENTRY_EXIT,
            );

        const uiTypes = logTypes
            .map(logType =>
                buildOption(logType, LogsHelper.getUiLogTypeName(logType)),
            )
            .sort((a, b) => a.label.localeCompare(b.label));

        if (uiTypes.length !== 1) uiTypes.unshift(allOption());

        return uiTypes;
    }

    function buildUiUserFilterOptions(users = []) {
        const uiUsers = users
            .map(user => buildOption(user.uid, user.email))
            .sort((a, b) => a.label.localeCompare(b.label));

        uiUsers.unshift(buildOption(nullValues.user));

        if (uiUsers.length !== 1) uiUsers.unshift(allOption());

        return uiUsers;
    }
}
